import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import Sidebar, { useSidebarCollapsed } from './Sidebar';
import TopBar from './TopBar';
import { useAuthStore } from '@/store/authStore';
import type { UserRole } from '@/store/authStore';

const WEB_ROLES: UserRole[] = [
  'platform_admin',
  'tenant_admin',
  'safety_manager',
  'supervisor',
  'executive',
  'ssr',
];

export default function AppShell() {
  const { userRole, signOut } = useAuthStore();
  const { collapsed, toggle } = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // SSR and viewer roles cannot use the web dashboard
  if (userRole && !WEB_ROLES.includes(userRole)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-center">
        <h1 className="text-xl font-semibold">Web access not available for your role</h1>
        <p className="text-sm text-muted-foreground">
          The MOZAIC web dashboard is available for SSRs, supervisors, safety managers, and administrators.
        </p>
        <button
          onClick={async () => { await signOut(); }}
          className="rounded-md bg-mozaic-teal px-4 py-2 text-sm text-white hover:bg-mozaic-teal/90"
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <div className="relative hidden md:block">
        <Sidebar collapsed={collapsed} onToggle={toggle} />
      </div>

      {/* Mobile sidebar (Sheet) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 p-0 [&>button]:hidden">
          <Sidebar
            collapsed={false}
            onToggle={toggle}
            onMobileClose={() => setMobileOpen(false)}
            isMobileSheet
          />
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar onMobileMenuOpen={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-y-auto bg-muted/30 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
